import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  HandCoins, 
  CheckCircle2, 
  ArrowRight, 
  Wrench, 
  Calendar, 
  Shield,
  Phone,
  Car,
  Gauge,
  Star
} from "lucide-react";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import CarsPage from "./pages/CarsPage";
import CarDetailPage from "./pages/CarDetailPage";
import SellYourCarPage from "./pages/SellYourCarPage";
import RepairsPage from "./pages/RepairsPage";
import ContactPage from "./pages/ContactPage";
import DashboardPage from "./pages/DashboardPage";
import DashboardLoginPage from "./pages/DashboardLoginPage";
import DashboardAddCarPage from "./pages/DashboardAddCarPage";
import DashboardCarsPage from "./pages/DashboardCarsPage";
import DashboardEditCarPage from "./pages/DashboardEditCarPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import NotFoundPage from "./pages/NotFoundPage";
import { Toaster } from "./components/ui/sonner";
import { DEALERSHIP_INFO } from "./config/dealership";

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Featured Cars Component - Premium Design
const FeaturedCars = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedCars = async () => {
      try {
        const response = await axios.get(`${API}/cars?featured=true&status=available&page_size=3`);
        setCars(response.data.cars || []);
      } catch (e) {
        console.error("Failed to fetch featured cars:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedCars();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="aspect-video bg-[#0A0A0A] animate-pulse" />
        ))}
      </div>
    );
  }

  if (cars.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {cars.map((car, index) => (
        <motion.div
          key={car.id}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          viewport={{ once: true }}
        >
          <Link
            to={`/cars/${car.id}`}
            className="group block card-luxury overflow-hidden"
            data-testid={`featured-car-${index}`}
          >
            <div className="aspect-video overflow-hidden relative">
              <img
                src={car.images?.[0] || "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800"}
                alt={`${car.make} ${car.model}`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
              
              {/* Price Badge */}
              <div className="absolute bottom-4 left-4">
                <span className="text-2xl font-serif font-medium text-white">
                  €{car.price?.toLocaleString()}
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="font-serif text-xl text-white mb-2 group-hover:text-[#CCAD60] transition-colors">
                {car.make} {car.model}
              </h3>
              <div className="flex items-center gap-4 text-zinc-500 text-sm">
                <span>{car.year}</span>
                <span>•</span>
                <span>{car.mileage?.toLocaleString()} mi</span>
                <span>•</span>
                <span>{car.fuel_type}</span>
              </div>
            </div>
            
            {/* Hover Border Effect */}
            <div className="absolute inset-0 border border-[#CCAD60]/0 group-hover:border-[#CCAD60]/30 transition-colors duration-500 pointer-events-none" />
          </Link>
        </motion.div>
      ))}
    </div>
  );
};

// Home Page Component
const Home = () => {
  const [healthStatus, setHealthStatus] = useState(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await axios.get(`${API}/health`);
        setHealthStatus(response.data);
      } catch (e) {
        console.error("Health check failed:", e);
      }
    };
    checkHealth();
  }, []);

  const services = [
    {
      icon: Car,
      title: 'Quality Vehicles',
      description: 'Handpicked selection of premium pre-owned vehicles, each thoroughly inspected.',
    },
    {
      icon: Wrench,
      title: 'Expert Service',
      description: 'Professional maintenance and repairs using OE quality parts.',
    },
    {
      icon: Shield,
      title: 'Peace of Mind',
      description: 'Transparent pricing and honest advice you can trust.',
    },
  ];

  const sellFeatures = [
    'Quick, no-obligation valuation',
    'Fair, competitive offers',
    'We handle all paperwork',
    'Same-day payment available',
  ];

  return (
    <div className="min-h-screen bg-[#000000]" data-testid="home-page">
      <Header />
      <main>
        <Hero />

        {/* Book Appointment CTA */}
        <section className="py-24 lg:py-32 bg-[#000000] relative overflow-hidden" data-testid="repairs-section">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23CCAD60' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }} />
          </div>
          
          <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <span className="label-gold mb-4 block">Service & Repairs</span>
              <h2 className="font-serif text-3xl lg:text-5xl text-white tracking-tight mb-6">
                Book Your Appointment
              </h2>
              <p className="text-zinc-400 mb-10 max-w-xl mx-auto">
                From routine maintenance to comprehensive repairs, our expert technicians 
                are here to keep your vehicle running at its best.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/repairs"
                  className="btn-gold inline-flex items-center justify-center gap-2"
                  data-testid="repairs-cta"
                >
                  <Calendar size={18} />
                  Book Service
                </Link>
                <a
                  href={DEALERSHIP_INFO.phoneHref}
                  className="btn-outline inline-flex items-center justify-center gap-2"
                >
                  <Phone size={18} />
                  {DEALERSHIP_INFO.phone}
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Trust / About Section */}
        <section className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="about-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <span className="label-gold mb-4 block">About Us</span>
                <h2 className="font-serif text-3xl lg:text-5xl text-white tracking-tight mb-6">
                  Your Trusted Partner in Automotive Excellence
                </h2>
                <p className="text-zinc-400 leading-relaxed mb-8">
                  At McCarthy Motors Midleton, we pride ourselves on delivering exceptional automotive 
                  care using only the highest quality OE products. Whether you're looking for your next 
                  vehicle or need expert maintenance, our dedicated team is here to help.
                </p>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="border-l-2 border-[#CCAD60]/30 pl-4">
                    <p className="font-serif text-2xl text-[#CCAD60] mb-1">5.0</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Google Rating</p>
                  </div>
                  <div className="border-l-2 border-[#CCAD60]/30 pl-4">
                    <p className="font-serif text-2xl text-[#CCAD60] mb-1">50+</p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Happy Reviews</p>
                  </div>
                </div>
              </motion.div>
              
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?q=80&w=2072&auto=format&fit=crop"
                    alt="About McCarthy Motors"
                    className="w-full h-full object-cover"
                    data-testid="about-image"
                  />
                </div>
                {/* Decorative Corner */}
                <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-[#CCAD60]/30" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Featured Vehicles Section */}
        <section className="py-24 lg:py-32 bg-[#000000]" data-testid="featured-cars-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12"
            >
              <div>
                <span className="label-gold mb-4 block">Our Collection</span>
                <h2 className="font-serif text-3xl lg:text-5xl text-white tracking-tight">
                  Featured Vehicles
                </h2>
              </div>
              <Link
                to="/cars"
                className="group inline-flex items-center gap-2 text-[#CCAD60] hover:text-white transition-colors"
                data-testid="view-all-cars-link"
              >
                <span className="text-sm font-medium uppercase tracking-wider">View All Stock</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
            
            <FeaturedCars />
          </div>
        </section>

        {/* Gold Line Separator */}
        <div className="gold-line" />

        {/* Services Section */}
        <section className="py-24 lg:py-32 bg-[#000000]" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="label-gold mb-4 block">Why Choose Us</span>
              <h2 className="font-serif text-3xl lg:text-5xl text-white tracking-tight mb-6">
                The McCarthy Difference
              </h2>
              <p className="text-zinc-400 max-w-2xl mx-auto">
                Delivering exceptional automotive care with integrity and expertise in Midleton, Co. Cork.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="group bg-[#0A0A0A] p-8 border-l-2 border-[#CCAD60]/20 hover:border-[#CCAD60] transition-colors duration-500"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-12 h-12 flex items-center justify-center border border-[#CCAD60]/20 mb-6 group-hover:border-[#CCAD60]/50 transition-colors">
                    <service.icon size={24} className="text-[#CCAD60]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-white mb-3">{service.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Sell Your Car Section */}
        <section className="py-24 lg:py-32 bg-[#0A0A0A]" data-testid="we-buy-cars-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              
              {/* Image */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative order-2 lg:order-1"
              >
                <div className="aspect-video overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1580273916550-e323be2ae537?q=80&w=1964&auto=format&fit=crop"
                    alt="Sell your car"
                    className="w-full h-full object-cover"
                    data-testid="we-buy-cars-image"
                  />
                </div>
                {/* Decorative Corner */}
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-[#CCAD60]/30" />
              </motion.div>
              
              {/* Content */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="order-1 lg:order-2"
              >
                <span className="label-gold mb-4 block">We Buy Cars</span>
                <h2 className="font-serif text-3xl lg:text-5xl text-white tracking-tight mb-6">
                  Selling Your Vehicle?
                </h2>
                <p className="text-zinc-400 leading-relaxed mb-8">
                  Get a fair, no-obligation offer for your car. We buy all makes and models, 
                  providing a hassle-free experience with same-day payment available.
                </p>
                
                <ul className="space-y-4 mb-10">
                  {sellFeatures.map((feature, index) => (
                    <motion.li
                      key={feature}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle2 size={18} className="text-[#CCAD60] shrink-0" />
                      <span className="text-zinc-300 text-sm">{feature}</span>
                    </motion.li>
                  ))}
                </ul>
                
                <Link
                  to="/sell-your-car"
                  className="btn-gold inline-flex items-center gap-2"
                  data-testid="we-buy-cars-cta"
                >
                  <HandCoins size={18} />
                  Get Your Offer
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Trust Poster Section - Warranty & Servicing */}
        <section className="py-20 lg:py-28 bg-[#000000]" data-testid="trust-poster-section">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center"
            >
              {/* Section Heading */}
              <span className="label-gold mb-4 block">Our Promise</span>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight mb-4 max-w-3xl mx-auto leading-tight">
                Manufacturer Warranty Protected.
                <br />
                <span className="text-zinc-400">Expert Servicing You Can Trust.</span>
              </h2>
              <p className="text-zinc-500 text-sm max-w-xl mx-auto mb-10">
                We use only OE quality parts and follow manufacturer specifications to keep your warranty intact.
              </p>

              {/* Poster Image Container */}
              <div className="relative group">
                {/* Gold glow effect on hover */}
                <div className="absolute -inset-1 bg-gradient-to-r from-[#CCAD60]/20 via-[#CCAD60]/10 to-[#CCAD60]/20 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                {/* Image Container */}
                <div className="relative rounded-lg overflow-hidden border border-[#CCAD60]/20 shadow-2xl shadow-black/50">
                  <img
                    src="https://customer-assets.emergentagent.com/job_cardealer-template/artifacts/oorex1ga_eff32459-859f-47b3-a667-46db3b875058.jpeg"
                    alt="McCarthy Motors - Top Auto Service Centre - Manufacturer Warranty Protected Servicing"
                    className="w-full h-auto"
                    data-testid="trust-poster-image"
                  />
                </div>
              </div>

              {/* CTA Button */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="mt-10"
              >
                <Link
                  to="/repairs"
                  className="btn-gold inline-flex items-center gap-2"
                  data-testid="trust-poster-cta"
                >
                  <Calendar size={18} />
                  Book a Service Appointment
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App" data-testid="app-root">
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/cars" element={
              <>
                <Header />
                <CarsPage />
                <Footer />
              </>
            } />
            <Route path="/cars/:id" element={
              <>
                <Header />
                <CarDetailPage />
                <Footer />
              </>
            } />
            <Route path="/sell-your-car" element={
              <>
                <Header />
                <SellYourCarPage />
                <Footer />
              </>
            } />
            <Route path="/repairs" element={
              <>
                <Header />
                <RepairsPage />
                <Footer />
              </>
            } />
            <Route path="/contact" element={
              <>
                <Header />
                <ContactPage />
                <Footer />
              </>
            } />
            <Route path="/privacy-policy" element={
              <>
                <Header />
                <PrivacyPolicyPage />
                <Footer />
              </>
            } />
            <Route path="/terms" element={
              <>
                <Header />
                <TermsPage />
                <Footer />
              </>
            } />
            {/* Dashboard Routes */}
            <Route path="/dashboard/login" element={<DashboardLoginPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/dashboard/add-car" element={<DashboardAddCarPage />} />
            <Route path="/dashboard/cars" element={<DashboardCarsPage />} />
            <Route path="/dashboard/edit-car/:id" element={<DashboardEditCarPage />} />
            {/* 404 Catch-all route */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <Toaster position="bottom-right" />
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
